/* eslint-disable max-lines */
import {
    useState, useRef, useEffect, useCallback
} from 'react';
import {
    useParams, useLocation
} from 'react-router-dom';
import {
    studioAgentService
} from 'shared/api';
import {
    PanelHeader, EmptyState, Composer, MessageBubble, WriteCard
} from './AgentParts';
import BexiDebugDialog from './BexiDebugDialog';
import BexiQuestionCard from './BexiQuestionCard';
import BexiChecklistPanel from './BexiChecklistPanel';
import SessionsSidebar from './SessionsSidebar';
import {
    useSessions
} from './useSessions';
import {
    useAttachments
} from './useAttachments';
import {
    useAutoApprove
} from './useAutoApprove';
import {
    buildStreamHandlers, toApiMessages
} from './chatHandlers';
import {
    derivePageContext
} from './pageContext';
import {
    BEXI_INTRO, MODELS, shuffledStarters
} from './bexiConstants';
import './agent.css';

let idSeq = 0;
const nextId = () => `m${Date.now()}_${idSeq++}`;
const SECRET_KEY = /(password|passwd|secret|token|api[_-]?key|authorization|credential)/i;

function redactDebugValue(value, depth = 0) {
    if (depth > 8) return '[Max depth]';
    if (Array.isArray(value)) return value.map((item) => redactDebugValue(item, depth + 1));
    if (!value || typeof value !== 'object') return value;
    const out = {};
    Object.entries(value).forEach(([key, item]) => {
        out[key] = SECRET_KEY.test(key) ? '[REDACTED]' : redactDebugValue(item, depth + 1);
    });
    return out;
}

/**
 * StudioAgentPanel — the BEXI chat surface (Phase 1: shell + feel).
 * Streaming text with a pulsing cursor, one status line for tool activity,
 * markdown replies, write-approval cards, empty state with starter chips, an
 * auto-growing composer with Send/Stop and a per-page saved draft.
 * Surface/positioning (dock, expand, mobile, backdrop) lives in StudioAgentDock.
 */
export default function StudioAgentPanel({
    onClose, onMinimize, expanded, onToggleExpand
}) {
    const {
        oragID
    } = useParams();
    const {
        pathname
    } = useLocation();
    const draftKey = `bexi:draft:${pathname}`;

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [model, setModel] = useState('glm');
    const [isLoading, setIsLoading] = useState(false);
    const [chips, setChips] = useState(() => shuffledStarters());
    const [stick, setStick] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [debugOpen, setDebugOpen] = useState(false);
    const [debugEvents, setDebugEvents] = useState([]);
    const [checklist, setChecklist] = useState(null);
    const {
        autoApprove, toggleAuto
    } = useAutoApprove();

    const {
        sessions, currentId, setCurrentId, pinned, togglePin, refresh, create, rename, remove,
    } = useSessions(oragID);
    const {
        attachments, addFiles, removeAttachment, clear: clearAttachments,
    } = useAttachments();

    const abortRef = useRef(null);
    const scrollRef = useRef(null);
    const endRef = useRef(null);
    const taRef = useRef(null);

    const started = messages.length > 0;
    // Latest messages for async callbacks (approve → auto-continue).
    const messagesRef = useRef([]);
    messagesRef.current = messages;
    const debugSeq = useRef(0);

    const addDebugEvent = useCallback((evt) => {
        const entry = {
            id: `d${Date.now()}_${debugSeq.current++}`,
            time: new Date().toISOString(),
            ...redactDebugValue(evt),
        };
        setDebugEvents((list) => [...list.slice(-399), entry]);
    }, []);

    const applyBexiControl = useCallback((evt) => {
        const input = evt.input || {};
        if (evt.toolName === 'bexi_set_checklist') {
            setChecklist({
                title: input.title || 'BEXI checklist',
                summary: input.summary || '',
                steps: (input.steps || []).map((step) => ({
                    ...step,
                    status: 'pending',
                    note: '',
                })),
            });
        }
        if (evt.toolName === 'bexi_update_checklist_step') {
            setChecklist((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    steps: prev.steps.map((step) => (step.id === input.id ? {
                        ...step,
                        status: input.status || step.status,
                        note: input.note || step.note,
                    } : step)),
                };
            });
        }
        if (evt.toolName === 'bexi_finish') {
            setChecklist((prev) => (prev ? {
                ...prev,
                finish: input,
            } : prev));
        }
    }, []);

    // Load per-page draft once.
    useEffect(() => {
        try {
            const d = localStorage.getItem(draftKey);
            if (d) setInput(d);
        } catch { /* storage may be unavailable */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey]);

    // Persist draft.
    useEffect(() => {
        try {
            if (input) localStorage.setItem(draftKey, input);
            else localStorage.removeItem(draftKey);
        } catch { /* ignore */ }
    }, [input, draftKey]);

    // Auto-grow the composer.
    useEffect(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    }, [input, started]);

    // Stick to bottom while streaming, unless the user scrolled up.
    useEffect(() => {
        if (stick) endRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [messages, stick]);

    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        setStick(nearBottom);
    }, []);

    const newChat = () => {
        if (isLoading) return;
        setMessages([]);
        setCurrentId(null);
        setChecklist(null);
        setChips(shuffledStarters());
    };

    // No auto-open: BEXI opens on a fresh empty chat. The dock keeps the panel
    // MOUNTED across minimize/restore, so the live chat is preserved without
    // reloading a past session. Past chats are still reachable via the recents
    // sidebar. (Minimizing hides the panel; the FAB brings this same chat back.)

    const switchTo = async (id) => {
        if (isLoading) return;
        try {
            const r = await studioAgentService.getSession(oragID, id);
            if (r?.ok && r.session) {
                setMessages((r.session.messages || []).map((m) => ({
                    id: nextId(),
                    role: m.role,
                    content: m.content
                })));
                setCurrentId(id);
                setChecklist(null);
                setSidebarOpen(false);
            }
        } catch { /* ignore */ }
    };

    const runTurn = async (text, opts = {}) => {
        const hidden = !!opts.hidden;
        // Only fully-read attachments go with a normal turn (never a hidden one).
        const atts = hidden ? [] : attachments
            .filter((a) => !a.reading && a.dataUrl)
            .map((a) => ({
                type: a.type,
                name: a.name,
                dataUrl: a.dataUrl
            }));
        if (!hidden) clearAttachments();
        const userMsg = {
            id: nextId(),
            role: 'user',
            content: text,
            hidden: hidden || undefined, // hidden continue-notes drive the model but aren't shown
            attachments: atts.length ? atts : undefined,
        };
        const liveId = nextId();
        const base = [...messagesRef.current, userMsg];
        setMessages([...base, {
            id: liveId,
            role: 'assistant',
            content: '',
            streaming: true,
            status: 'Thinking…'
        }]);
        addDebugEvent({
            type: 'turn-start',
            hidden,
            model: opts.modelOverride || model,
            sessionId: currentId,
            messageCount: base.length,
            userText: text,
        });
        setInput('');
        setStick(true);
        setIsLoading(true);

        // Ensure a server session exists so history persists.
        let sid = currentId;
        if (!sid) {
            try {
                const s = await create();
                if (s?.id) { sid = s.id; setCurrentId(sid); }
            } catch { /* history is best-effort */ }
        }

        const setLive = (fn) => setMessages((m) => m.map((msg) => (msg.id === liveId ? fn(msg) : msg)));
        // Idle indicator: if no stream event arrives for a beat, show "Thinking…"
        // so a reasoning pause never looks like a frozen cursor.
        let think;
        const patchLive = (fn) => {
            clearTimeout(think);
            setLive(fn);
            think = setTimeout(() => setLive((msg) => (msg.streaming && !msg.status ? {
                ...msg,
                status: 'Thinking…'
            } : msg)), 1200);
        };

        const controller = new window.AbortController();
        abortRef.current = controller;

        const handlers = buildStreamHandlers(patchLive);
        handlers.signal = controller.signal;
        handlers.onDebugEvent = addDebugEvent;
        const baseToolCall = handlers.onToolCall;
        handlers.onToolCall = (evt) => {
            applyBexiControl(evt);
            baseToolCall?.(evt);
        };
        const pageContext = derivePageContext(pathname);
        // A recovery continue (after a provider error) forces the fallback model.
        const useModel = opts.modelOverride || model;
        await studioAgentService.streamChat(oragID, toApiMessages(base), useModel, handlers, sid, atts, pageContext, autoApprove);
        clearTimeout(think);
        abortRef.current = null;

        // Auto-resume on ANY abnormal finish so a single hiccup never dumps the
        // user out of a task:
        //   - 'tool-calls' → hit the step cap mid-work.
        //   - 'error' / 'other' / 'length' / 'unknown' → the provider rejected or
        //     cut the turn (e.g. a Novita/Qwen 400 on a large step).
        // A plain 'stop' means the model finished OR asked the user a question —
        // never auto-continue that (it caused "I'm waiting for you" loops).
        //
        // The continue is a FRESH turn built from saved history, so it never
        // replays writes that already ran. A PROVIDER error resumes on the OTHER
        // model (glm <-> qwen) to get past whatever tripped the primary one.
        // Pending writes (approval-gated) pause the loop — the user must approve
        // those. Bounded by depth so a persistent failure can't spin.
        const oc = handlers.outcome;
        const depth = opts.autoDepth || 0;
        const fr = oc.finishReason;
        const hasPending = (oc.pendingWrites || []).length > 0;
        const hasQuestion = (oc.pendingQuestions || []).length > 0;
        const hasFinish = !!oc.finish;
        const recoverable = fr && fr !== 'stop';
        if (recoverable && !hasPending && !hasQuestion && !hasFinish && depth < 8 && !controller.signal.aborted) {
            // A provider error resumes on the OTHER model (glm <-> qwen) — a fresh
            // model that won't wedge on the same call. Both are on Novita.
            const providerError = fr === 'error' || fr === 'other' || fr === 'unknown';
            const fallback = model === 'glm' ? 'qwen' : 'glm';
            runTurn('Continue with the task until it is fully complete.', {
                hidden: true,
                autoDepth: depth + 1,
                modelOverride: providerError ? fallback : undefined,
            });
            return;
        }

        // Stop-short recovery. The model ended a turn ('stop') without closing it
        // — it asked a question in plain text (no card), promised an action it
        // never took, or did work and forgot bexi_finish. A plain 'stop' is never
        // auto-resumed above (to avoid "waiting for you" loops), so without this
        // the user dead-ends staring at prose with nothing to answer. The server
        // flags this precise shape as `stalled`; nudge ONCE (bounded, same model)
        // to turn the prose question into a bexi_ask_user card, or to finish. A
        // model that was genuinely done just calls bexi_finish next → clean stop.
        if (oc.stalled && fr === 'stop' && !hasPending && !hasQuestion && !hasFinish
            && depth < 6 && !controller.signal.aborted) {
            runTurn(
                'You ended the turn without finishing it. If you have a question for the user, '
                + 'ask it with bexi_ask_user (NEVER as plain text — a typed question shows no card and the user cannot answer). '
                + 'Otherwise keep working, and call bexi_finish when the task is fully done.',
                { hidden: true, autoDepth: depth + 1 },
            );
            return;
        }

        setIsLoading(false);
        refresh(); // server persisted + auto-titled before the stream ended
    };

    const send = () => {
        const text = input.trim();
        const hasReady = attachments.some((a) => !a.reading && a.dataUrl);
        if ((!text && !hasReady) || isLoading) return;
        runTurn(text);
    };

    const stop = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsLoading(false);
        setMessages((m) => m.map((msg) => (msg.streaming
            ? {
                ...msg,
                streaming: false,
                status: null,
                content: msg.content ? `${msg.content} _[stopped]_` : '_[stopped]_'
            }
            : msg)));
    };

    const approveWrite = async (msgId, write) => {
        setIsLoading(true);
        let res;
        try {
            res = await studioAgentService.runTool(oragID, write.tool, write.args || {});
        } catch (e) {
            res = {
                ok: false,
                error: e?.message || String(e)
            };
        }
        addDebugEvent({
            type: 'approved-write-result',
            tool: write.tool,
            args: write.args || {},
            response: res,
        });
        // Mark the card done/failed.
        setMessages((m) => m.map((msg) => (msg.id === msgId
            ? {
                ...msg,
                pendingWrites: (msg.pendingWrites || []).map((w) => (w === write ? {
                    ...w,
                    done: true,
                    ok: res.ok
                } : w))
            }
            : msg)));
        setIsLoading(false);

        // Auto-continue: feed the result back (hidden) so the agent keeps going.
        const outcome = res.ok
            ? `The action "${write.tool}" was approved and executed. Result: ${JSON.stringify(res.result ?? {}).slice(0, 1500)}.`
            : `The action "${write.tool}" failed: ${res.error || 'error'}.`;
        runTurn(`${outcome} Continue with the plan; if everything is done, briefly summarize what you did.`, {
            hidden: true
        });
    };

    // One line of transcript for a single answered question.
    const answerLine = (q) => {
        const a = q.answer || {};
        const labels = (a.chosen || []).map((opt) => opt.label).join(', ');
        const other = a.other ? ` Other: ${a.other}` : '';
        const detail = q.detail ? `\nQuestion detail/plan:\n${q.detail}` : '';
        return `Answer to "${a.question || q.question}": ${labels || 'Custom answer'}.${other}${detail}`;
    };
    const answerQuestion = (msgId, question, answer) => {
        // A turn can raise SEVERAL questions at once (multiple cards). Record each
        // answer as its card is submitted, but only send the turn once EVERY card
        // is answered — otherwise the first Continue would fire with a single
        // answer and orphan the rest. Completeness is read from the live ref (a
        // functional setState updater runs during render, so it cannot be read
        // back synchronously here).
        const msg = (messagesRef.current || []).find((x) => x.id === msgId);
        const pqs = ((msg && msg.pendingQuestions) || []).map((q) => (q === question ? { ...q, answered: true, answer } : q));
        setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, pendingQuestions: pqs } : x)));
        addDebugEvent({ type: 'question-answer', questionId: question.id, answer });
        if (pqs.length && pqs.every((q) => q.answered)) {
            const body = pqs.map(answerLine).join('\n\n');
            runTurn(`${body}\nContinue.`, { hidden: false });
        }
    };

    const rejectWrite = (msgId, write) => {
        setMessages((m) => m.map((msg) => (msg.id === msgId
            ? {
                ...msg,
                pendingWrites: (msg.pendingWrites || []).map((w) => (w === write ? {
                    ...w,
                    rejected: true
                } : w))
            }
            : msg)));
    };

    const onComposerKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    return (
        <div className="flex h-full flex-col bg-background">
            <PanelHeader
                onToggleSidebar={() => setSidebarOpen((v) => !v)}
                model={model}
                setModel={setModel}
                models={MODELS}
                autoApprove={autoApprove}
                onToggleAuto={toggleAuto}
                onNewChat={newChat}
                isLoading={isLoading}
                onToggleExpand={onToggleExpand}
                expanded={expanded}
                onMinimize={onMinimize}
                onClose={onClose}
                onOpenDebug={() => setDebugOpen(true)}
                debugCount={debugEvents.length}
            />

            {/* Row: recents sidebar + (body over composer).
                Full-screen (expanded) always shows recents on the side. */}
            <div className="flex flex-1 overflow-hidden">
                {(expanded || sidebarOpen) && (
                    <div className={expanded ? 'w-64 shrink-0' : 'w-56 shrink-0'}>
                        <SessionsSidebar
                            sessions={sessions}
                            currentId={currentId}
                            pinned={pinned}
                            onNew={() => { newChat(); setSidebarOpen(false); }}
                            onSwitch={switchTo}
                            onRename={rename}
                            onDelete={(id) => { remove(id); if (id === currentId) newChat(); }}
                            onTogglePin={togglePin}
                        />
                    </div>
                )}

                <div className="flex flex-1 flex-col overflow-hidden">
                    <BexiChecklistPanel checklist={checklist} />
                    {/* Body */}
                    {!started ? (
                        <EmptyState chips={chips} onPick={(c) => runTurn(c)} intro={BEXI_INTRO} />
                    ) : (
                        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-3 py-3">
                            <div className={`mx-auto space-y-3 ${expanded ? 'max-w-3xl' : ''}`}>
                                {messages.map((m) => (m.hidden ? null : (
                                    <div key={m.id}>
                                        <MessageBubble m={m} />
                                        {(m.pendingWrites || []).map((w, i) => (
                                            <WriteCard
                                                key={i}
                                                w={w}
                                                disabled={isLoading}
                                                onApprove={() => approveWrite(m.id, w)}
                                                onReject={() => rejectWrite(m.id, w)}
                                            />
                                        ))}
                                        {(() => {
                                            // While a batch has more than one unanswered card, each button
                                            // SAVES its answer (the turn is sent only once all are answered),
                                            // so label it accordingly instead of a misleading "Continue".
                                            const unanswered = (m.pendingQuestions || []).filter((q) => !q.answered).length;
                                            return (m.pendingQuestions || []).map((q, i) => (q.answered ? null : (
                                                <BexiQuestionCard
                                                    key={q.id || i}
                                                    question={q}
                                                    disabled={isLoading}
                                                    submitLabel={unanswered > 1 ? 'Save answer' : 'Continue'}
                                                    onAnswer={(answer) => answerQuestion(m.id, q, answer)}
                                                />
                                            )));
                                        })()}
                                    </div>
                                )))}
                                <div ref={endRef} />
                            </div>
                        </div>
                    )}

                    {/* Composer */}
                    <Composer
                        taRef={taRef}
                        value={input}
                        onChange={setInput}
                        onKeyDown={onComposerKey}
                        onSend={send}
                        onStop={stop}
                        isLoading={isLoading}
                        tall={!started}
                        expanded={expanded}
                        attachments={attachments}
                        onAddFiles={addFiles}
                        onRemoveAttachment={removeAttachment}
                    />
                </div>
            </div>
            <BexiDebugDialog
                open={debugOpen}
                onOpenChange={setDebugOpen}
                events={debugEvents}
                onClear={() => setDebugEvents([])}
            />
        </div>
    );
}
